import type {
  CanonicalReportProduct,
  CanonicalResearchReportDocument,
  ResearchReportAdminOverrides,
  SeoGeneratedComponents,
  SeoGeneratedProductImages,
  SeoFaqEntry,
  SeoProjection,
  SeoTopPickRole,
  SeoTopPickBuyLink,
  SeoEvidence,
  SeoComparisonTable,
  SeoSkipProduct,
  SeoNotableMention,
  SeoArchetype,
  SeoReportVisualSignalsContent,
} from './types'
import {
  getSeoReportCategory,
  getSeoReportSubcategory,
} from './category-taxonomy'
import { getResearchAuthor, type ResearchAuthor } from './authors'
import { normalizeFaqCandidates, normalizeNullableString } from './normalize'
import { buildSeoReportUrl } from './paths'
import { decodeAndStripHtml } from './sanitize-display-text'
import { buildTheBestTitle } from './title'
import { hasVisualSignalsChartPayload } from './visual-signals'

export const FRAGRANCE_SUBCATEGORY_SLUG = 'fragrance'

/**
 * The report's own subcategory: the stored column, then the canonical
 * document, then the generated classification. This is what the report IS,
 * independent of any admin category override (an override that moves the
 * report to another category nulls the *projected* `meta.subcategorySlug`
 * because the report leaves the inherited category's URL tree). Evidence
 * rules that depend on the product category — fragrance score suppression —
 * must key on this value, never on `meta.subcategorySlug`.
 */
export function resolveReportSubcategorySlug(report: {
  subcategorySlug?: string | null
  document?: Pick<CanonicalResearchReportDocument, 'meta'> | null
  generatedComponents?: Pick<
    SeoGeneratedComponents,
    'category_classification'
  > | null
}): string | null {
  return (
    report.subcategorySlug ??
    report.document?.meta.subcategorySlug ??
    report.generatedComponents?.category_classification?.subcategorySlug ??
    null
  )
}

/**
 * Fragrance reports carry no evidence-backed similarity or fit numbers, so
 * the projection withholds every numeric score for them: fitScore
 * (and with it notable-mention match scores and the Product.review JSON-LD),
 * the visual-signals charts, and `/100` relative comparison dimensions.
 */
export function isFragranceReport(
  report: Parameters<typeof resolveReportSubcategorySlug>[0]
): boolean {
  return resolveReportSubcategorySlug(report) === FRAGRANCE_SUBCATEGORY_SLUG
}

const DEFAULT_DESCRIPTION_FALLBACK =
  'Dupe analyzed structured product data and community signals to surface the best options for this search.'

function truncate(input: string, maxLength: number): string {
  if (input.length <= maxLength) return input
  if (maxLength <= 3) return '.'.repeat(maxLength)
  return `${input.slice(0, maxLength - 3).trimEnd()}...`
}

/**
 * Presentation order for report products: ranked first (ascending rank),
 * then unranked by fit score. This is the order the public projection
 * renders — top picks are the first 4, notable mentions the next 3.
 * Exported so server-side consumers (e.g. the retailer-links refresh) can
 * target exactly the publicly visible product window.
 */
export function rankProducts(
  products: CanonicalReportProduct[]
): CanonicalReportProduct[] {
  return [...products].sort((a, b) => {
    const rankA = a.ranking?.rank ?? Number.MAX_SAFE_INTEGER
    const rankB = b.ranking?.rank ?? Number.MAX_SAFE_INTEGER
    if (rankA !== rankB) return rankA - rankB

    const scoreA = a.ranking?.fitScore ?? Number.NEGATIVE_INFINITY
    const scoreB = b.ranking?.fitScore ?? Number.NEGATIVE_INFINITY
    return scoreB - scoreA
  })
}

function uniqueStrings(values: Array<string | null | undefined>): string[] {
  const seen = new Set<string>()
  const result: string[] = []
  for (const value of values) {
    const trimmed = typeof value === 'string' ? value.trim() : ''
    if (!trimmed || seen.has(trimmed)) continue
    seen.add(trimmed)
    result.push(trimmed)
  }
  return result
}

const DURABLE_IMAGE_HOSTS = ['ik.imagekit.io', 'assets.dupe.com'] as const

/**
 * Is this image served from our own ImageKit storage/proxy (or a local
 * asset path), i.e. immune to third-party link rot and hotlink blocks?
 */
function isDurableImageUrl(url: string): boolean {
  if (url.startsWith('/')) return true
  try {
    const hostname = new URL(url).hostname.toLowerCase()
    return DURABLE_IMAGE_HOSTS.some(
      (host) => hostname === host || hostname.endsWith(`.${host}`)
    )
  } catch {
    return false
  }
}

/**
 * Stable-partition a product's images so durable (ImageKit-hosted) URLs
 * render before raw third-party URLs. `research_products.images` keeps
 * ranked source URLs verbatim, and the async rehost leaves a raw URL in
 * place when its upload fails (hotlink protection, link rot) — which can
 * pin a dead image at slot 0. Every render surface picks `images[0]`
 * (top picks, notable mentions, skip product, JSON-LD), so prefer any
 * durable image over a fragile raw one. Relative order is preserved
 * within each group.
 */
export function preferDurableImages(images: string[]): string[] {
  const durable: string[] = []
  const fragile: string[] = []
  for (const url of images) {
    if (isDurableImageUrl(url)) durable.push(url)
    else fragile.push(url)
  }
  if (durable.length === 0 || fragile.length === 0) return images
  return [...durable, ...fragile]
}

function overlayGeneratedProductImages(
  products: CanonicalReportProduct[],
  generatedProductImages: SeoGeneratedProductImages | null | undefined
): CanonicalReportProduct[] {
  if (
    generatedProductImages?.status !== 'ready' ||
    generatedProductImages.displaySource === 'source'
  ) {
    return products
  }

  const generatedBySlug = new Map(
    (generatedProductImages.products ?? []).map((product) => [
      product.slug,
      product,
    ])
  )

  return products.map((product) => {
    const generated = generatedBySlug.get(product.slug)
    const generatedUrls = (generated?.images ?? [])
      .map((image) => image.assetUrl)
      .filter((url) => url.trim().length > 0)
    if (generatedUrls.length === 0) return product

    return {
      ...product,
      images: uniqueStrings([...generatedUrls, ...product.images]),
    }
  })
}

function buildRoleLabel(role: SeoTopPickRole): string {
  switch (role) {
    case 'best-overall':
      return 'Best Overall'
    case 'runner-up':
      return 'Runner Up'
    case 'high-end':
      return 'Alternate Angle'
    case 'budget':
      return 'Alternate Angle'
    case 'best-for':
      return 'Alternate Angle'
    default:
      return 'Worth a look'
  }
}

function normalizeSpecKey(key: string): string {
  return key.replace(/[\s_-]+/g, '').toLowerCase()
}

/**
 * Strings the upstream taxonomy classifier emits as a "no" answer. Keep
 * this conservative — anything not matched here flows through as a real
 * value. Booleans, numbers, and longer-form strings are never negative.
 */
function isNegativeSpecValue(value: unknown): boolean {
  if (value == null) return true
  if (typeof value === 'boolean' && !value) return true
  if (typeof value === 'string') {
    const trimmed = value.trim().toLowerCase()
    if (trimmed === '') return true
    return (
      trimmed === 'no' ||
      trimmed === 'false' ||
      trimmed === 'n/a' ||
      trimmed === 'na' ||
      trimmed === 'none' ||
      trimmed === '-' ||
      trimmed === '—' ||
      trimmed === 'null'
    )
  }
  return false
}

/**
 * Identifies spec keys whose values are uniformly "negative" (No / false /
 * null / empty / n/a) across every product in the report that has the key
 * set. These are upstream taxonomy-classifier outputs ("Clogs: No",
 * "Wireless: No") that carry no discriminating info on a report whose
 * products are all _not_ that subtype.
 *
 * Why this shape and not a hardcoded vocab keyed by subcategory: the same
 * heuristic generalizes to every product category without per-subcategory
 * denylists ("clogs/flats/mules" for shoes, "wired/noise-cancelling" for
 * headphones, etc.). It only ever drops keys that are negative across the
 * board, so legitimate `Yes` / numeric / non-trivial answers always
 * survive — and a single product's standalone `Foo: No` (which might be
 * legitimately informative) survives too.
 */
function findUninformativeSpecKeys(
  products: CanonicalReportProduct[]
): Set<string> {
  const valuesByKey = new Map<string, unknown[]>()
  for (const product of products) {
    if (!product.specs) continue
    for (const [key, value] of Object.entries(product.specs)) {
      const norm = normalizeSpecKey(key)
      const list = valuesByKey.get(norm) ?? []
      list.push(value)
      valuesByKey.set(norm, list)
    }
  }
  const result = new Set<string>()
  for (const [norm, values] of valuesByKey) {
    if (values.length < 2) continue
    if (values.every(isNegativeSpecValue)) result.add(norm)
  }
  return result
}

/**
 * Drop spec keys flagged as uniformly-negative across the report. Pass
 * the precomputed key set from `findUninformativeSpecKeys` — that's
 * computed once over the full product list and reused for every product
 * mapping pass.
 */
export function dropTaxonomyClassifierSpecs(
  specs: CanonicalReportProduct['specs'],
  uninformativeKeys: ReadonlySet<string>
): CanonicalReportProduct['specs'] {
  if (!specs || uninformativeKeys.size === 0) return specs
  const filtered: NonNullable<CanonicalReportProduct['specs']> = {}
  for (const [key, value] of Object.entries(specs)) {
    if (uninformativeKeys.has(normalizeSpecKey(key))) continue
    filtered[key] = value
  }
  return filtered
}

export { findUninformativeSpecKeys }

/** Legacy title suffix variants stripped from stored/admin-supplied titles. */
export const DUPE_TITLE_SUFFIX = ' — Dupe Research'

const DUPE_TITLE_SUFFIX_VARIANTS = [
  '— Dupe Research',
  '- Dupe Research',
] as const

/** Strip the " — Dupe Research" / " - Dupe Research" suffix from a title. */
export function stripDupeTitleSuffix(title: string): string {
  const trimmed = title.trim()
  for (const suffix of DUPE_TITLE_SUFFIX_VARIANTS) {
    if (trimmed.endsWith(suffix)) {
      return trimmed.slice(0, -suffix.length).trim()
    }
  }
  return trimmed
}

/** Static positional roles: Best Overall, Runner Up, Alternate Angle, Worth a look */
function buildTopPickRoles(picks: CanonicalReportProduct[]): SeoTopPickRole[] {
  const positionalRoles: SeoTopPickRole[] = [
    'best-overall',
    'runner-up',
    'best-for',
    'alternative',
  ]
  return picks.map((_, i) => positionalRoles[i] ?? 'alternative')
}

function buildSeoTitle(
  document: CanonicalResearchReportDocument,
  keyword: string | null,
  h1: string,
  overrides?: ResearchReportAdminOverrides | null,
  generatedComponents?: SeoGeneratedComponents | null
): string {
  // Priority order:
  //   1. Admin override (explicit editorial decision — highest authority)
  //   2. Meta-agent-generated `seo_title` (ready + non-empty)
  //   3. H1 / keyword / document.meta.title normalized to "The Best ..."
  if (overrides?.seoTitleOverride) return overrides.seoTitleOverride

  const generated = normalizeNullableString(
    generatedComponents?.seo_title?.status === 'ready'
      ? generatedComponents.seo_title.content?.text
      : null
  )
  if (generated) return truncate(generated, 60)

  // Prefer the H1 over the bare keyword: H1s carry the qualifier (e.g. "...
  // Clover Bracelets") that the keyword sometimes lacks.
  const base =
    normalizeNullableString(h1) ??
    normalizeNullableString(keyword) ??
    document.meta.title
  return truncate(buildTheBestTitle(base), 60)
}

function buildMetaDescription(
  document: CanonicalResearchReportDocument,
  keyword: string | null,
  overrides?: ResearchReportAdminOverrides | null,
  generatedComponents?: SeoGeneratedComponents | null
): string {
  // Priority order mirrors buildSeoTitle: admin override → meta-agent →
  // verdictSummary-based legacy fallback.
  if (overrides?.metaDescriptionOverride)
    return overrides.metaDescriptionOverride

  const generated = normalizeNullableString(
    generatedComponents?.meta_description?.status === 'ready'
      ? generatedComponents.meta_description.content?.text
      : null
  )
  // Generated descriptions are validated to <= 170 chars at generation
  // time; truncating again at 160 was clipping the 161-170 band with an
  // ellipsis mid-sentence (77 reports after the June 2026 backfill). The
  // 175 bound is a safety net for malformed payloads only.
  if (generated) return truncate(generated, 175)

  const base = document.analysis?.verdictSummary ?? DEFAULT_DESCRIPTION_FALLBACK
  const prefix = keyword ? `${keyword}: ` : ''
  return truncate(`${prefix}${base}`, 160)
}

function buildH1(
  document: CanonicalResearchReportDocument,
  keyword: string | null,
  overrides?: ResearchReportAdminOverrides | null
): string {
  const override = normalizeNullableString(overrides?.h1Override)
  if (override) {
    return /^the best\s+/i.test(override)
      ? override
      : buildTheBestTitle(override)
  }

  // The normal public path should have an agent-generated h1Override. This
  // fallback stays mechanical on purpose so title-casing does not grow a
  // second keyword/connector-word list that can drift from the generation
  // prompt.
  return buildTheBestTitle(
    normalizeNullableString(keyword) ?? document.meta.title
  )
}

function buildQuickAnswer(
  document: CanonicalResearchReportDocument,
  topPick: CanonicalReportProduct | undefined
) {
  // Prefer the quick decision copy from the report pipeline -- it's richer
  const qd = document.analysis?.quickDecisionCopy
  if (qd && (qd.decisionPrefix || qd.title)) {
    // Only assemble prefix+name+suffix when topPick exists, otherwise the
    // sentence template produces broken grammar (prefix glued to suffix).
    const body =
      topPick?.name && (qd.decisionPrefix || qd.decisionSuffix)
        ? [qd.decisionPrefix, topPick.name, qd.decisionSuffix]
            .filter(Boolean)
            .join(' ')
        : (qd.decisionPrefix ?? qd.decisionSuffix ?? null)

    if (body) {
      return {
        title:
          qd.title ??
          (topPick ? `${topPick.name} is the top pick` : 'Quick answer'),
        body,
        productSlug: topPick?.slug ?? null,
      }
    }
  }

  const summary =
    topPick?.ranking?.highlightText ?? topPick?.ranking?.rationale ?? null

  if (!summary) return undefined

  return {
    title: topPick ? `${topPick.name} is the top pick` : 'Quick answer',
    body: summary,
    productSlug: topPick?.slug ?? null,
  }
}

function buildGeneratedQuickAnswer(
  generatedComponents: SeoGeneratedComponents | null | undefined
) {
  const title = normalizeNullableString(
    generatedComponents?.quick_answer?.content?.title
  )
  const body = normalizeNullableString(
    generatedComponents?.quick_answer?.content?.body
  )

  if (!title || !body) {
    return undefined
  }

  // Note: legacy payloads may still contain a `dupeActivityVerdict` blob.
  // It is intentionally not projected -- the user count was fabricated
  // (random), so it must never reach the page or its JSON-LD again.
  return {
    title,
    body,
    productSlug:
      normalizeNullableString(
        generatedComponents?.quick_answer?.content?.productSlug
      ) ?? null,
  }
}

function buildFaq(document: CanonicalResearchReportDocument): SeoFaqEntry[] {
  const fromAnalysis = normalizeFaqCandidates(document.analysis?.faqCandidates)
  if (fromAnalysis.length > 0) return fromAnalysis.slice(0, 5)

  const guide = document.methodology?.buyingGuide
  if (!guide) return []

  return guide.insights.slice(0, 5).map((insight) => ({
    question: insight.heading,
    answer: insight.body,
  }))
}

function buildGeneratedFaq(
  generatedComponents: SeoGeneratedComponents | null | undefined
): SeoFaqEntry[] {
  const items = generatedComponents?.faq?.items
  if (!Array.isArray(items)) return []

  return items
    .flatMap((item): SeoFaqEntry[] => {
      const question = normalizeNullableString(item?.question)
      const answer = normalizeNullableString(item?.answer)
      if (!question || !answer) return []
      return [
        {
          question,
          answer,
        },
      ]
    })
    .slice(0, 5)
}

/**
 * Decode HTML entities and strip stray tags from FAQ copy. FAQ answers are
 * authored as plain text, but upstream sources sometimes leak entities/tags that
 * would otherwise render literally on the page and in the FAQPage JSON-LD (both
 * read this same array). Entries left empty after sanitizing are dropped so the
 * JSON-LD never emits a blank Question/Answer.
 */
function sanitizeFaqEntries(entries: SeoFaqEntry[]): SeoFaqEntry[] {
  return entries.flatMap((entry): SeoFaqEntry[] => {
    const question = decodeAndStripHtml(entry.question).trim()
    const answer = decodeAndStripHtml(entry.answer).trim()
    if (!question || !answer) return []
    return [{ ...entry, question, answer }]
  })
}

function buildGeneratedVisualSignals(
  generatedComponents: SeoGeneratedComponents | null | undefined
): SeoReportVisualSignalsContent | undefined {
  const visualSignals = generatedComponents?.visual_signals
  if (visualSignals?.status !== 'ready' || !visualSignals.content) {
    return undefined
  }

  return hasVisualSignalsChartPayload(visualSignals.content)
    ? visualSignals.content
    : undefined
}

function buildEvidence(document: CanonicalResearchReportDocument): SeoEvidence {
  return {
    productsAnalyzed: document.products.length,
    sourcesReviewed: document.evidence.totalSourcesCount,
    youtubeCount: document.evidence.totalYoutubeCount,
    redditCount: document.evidence.totalRedditCount,
    tiktokCount: document.evidence.totalTiktokCount,
    instagramCount: document.evidence.totalInstagramCount,
    researchDurationMs: deriveResearchDurationMs(document),
  }
}

function deriveResearchDurationMs(
  document: CanonicalResearchReportDocument
): number | null {
  const startedAt = document.meta.createdAt
  const completedAt = document.meta.completedAt
  if (!startedAt || !completedAt) return null
  const startMs = new Date(startedAt).getTime()
  const endMs = new Date(completedAt).getTime()
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) return null
  if (endMs <= startMs) return null
  return endMs - startMs
}

function buildFreshnessStatement(
  document: CanonicalResearchReportDocument
): string {
  const sources = document.evidence.totalSourcesCount
  const products = document.products.length
  const dateStr =
    document.sourceSnapshot.taskCompletedAt ?? document.meta.completedAt
  const formatted = dateStr
    ? new Date(dateStr).toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      })
    : null
  const datePart = formatted ? `, as of ${formatted}` : ''
  return `Based on analysis of ${sources} sources covering ${products} products${datePart}.`
}

function buildArchetype(
  document: CanonicalResearchReportDocument
): SeoArchetype | undefined {
  const raw = document.meta.archetype
  if (!raw) return undefined
  const firstSentence = raw.split(/\.\s/)[0] ?? ''
  const name =
    firstSentence.length > 40
      ? firstSentence.slice(0, 37) + '...'
      : firstSentence
  return { name, description: raw }
}

function buildTopPickBuyLinks(
  product: CanonicalReportProduct
): SeoTopPickBuyLink[] {
  const links = product.retailerLinks?.buyLinks
  if (!Array.isArray(links)) return []
  return links.slice(0, 3).map((link) => ({
    label: link.label,
    url: link.url,
    price: link.price ?? null,
  }))
}

function buildTopPickRoleDescription(
  product: CanonicalReportProduct,
  productSummary: string | null
): string | null {
  return (
    normalizeNullableString(product.ranking?.highlightText) ??
    normalizeNullableString(product.ranking?.highlight) ??
    productSummary?.split(/\.\s/)[0] ??
    null
  )
}

const COMPARISON_TABLE_MAX_ROWS = 6

const COMPARISON_TABLE_SKIP_SPEC_KEYS = new Set([
  'color',
  'colour',
  'model',
  'item no.',
  'item number',
  'series',
  'compatibility',
  'manufacturer',
  'electrical ratings',
  'voltage frequency',
  'power cord',
  'cord',
  'use',
  'product weight',
  'product dimensions',
  'rating',
  'average rating',
  'review count',
  'reviews',
  'rating count',
  'price',
  'msrp',
])

function formatComparisonLabel(key: string): string {
  return key
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase())
}

function normalizeComparisonSpecKey(key: string): string {
  return key
    .toLowerCase()
    .trim()
    .replace(/\s*\([^)]*\)\s*$/, '')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

function formatComparisonValue(
  value: string | number | boolean | null | undefined
): string | null {
  if (value == null) return null
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return null
    return Number.isInteger(value)
      ? String(value)
      : String(Number(value.toFixed(2)))
  }

  const trimmed = value.trim()
  if (!trimmed) return null
  return truncate(trimmed, 120)
}

function firstComparisonValue(
  values: Array<string | null | undefined>
): string | null {
  for (const value of values) {
    const formatted = formatComparisonValue(value)
    if (formatted) return formatted
  }
  return null
}

function formatCurrencyAmount(value: number, currency: string): string {
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      maximumFractionDigits: Number.isInteger(value) ? 0 : 2,
    }).format(value)
  } catch {
    return `$${Number.isInteger(value) ? value : value.toFixed(2)}`
  }
}

function formatProductPriceRange(
  product: CanonicalReportProduct
): string | null {
  const currency = derivePriceCurrency(product)
  const low = product.pricing.observedLow ?? product.pricing.msrp ?? null
  const high = product.pricing.observedHigh ?? null

  if (low == null && high == null) return null
  if (low != null && high != null && low !== high) {
    return `${formatCurrencyAmount(low, currency)}-${formatCurrencyAmount(
      high,
      currency
    )}`
  }

  const value = low ?? high
  return value == null ? null : formatCurrencyAmount(value, currency)
}

function buildComparisonPriorityLabels(
  document: CanonicalResearchReportDocument
): string[] {
  const brief = document.methodology?.brief
  return uniqueStrings([
    ...(brief?.visual_priority_dimensions ?? []),
    ...(brief?.answer_first_concerns ?? []),
    ...(brief?.top_concerns ?? []),
    ...(brief?.choice_drivers?.map((driver) => driver.name) ?? []),
    ...(brief?.dimensions ?? []),
    ...(brief?.what_matters ?? []),
  ])
}

function tokenizeComparisonPriority(label: string): string[] {
  return label
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .map((part) => part.trim())
    .filter((part) => part.length >= 4)
}

function scoreComparisonPriority(
  label: string,
  priorityLabels: string[]
): number {
  const normalizedLabel = label.toLowerCase()
  let score = 0

  for (const priority of priorityLabels) {
    const normalizedPriority = priority.trim().toLowerCase()
    if (!normalizedPriority) continue

    if (normalizedLabel.includes(normalizedPriority)) {
      score += 30
    }

    for (const token of tokenizeComparisonPriority(normalizedPriority)) {
      if (normalizedLabel.includes(token)) {
        score += 8
      }
    }
  }

  return score
}

function getComparisonPriorityIndex(
  label: string,
  priorityLabels: string[]
): number {
  const normalizedLabel = label.toLowerCase()

  for (let index = 0; index < priorityLabels.length; index += 1) {
    const normalizedPriority = priorityLabels[index]?.trim().toLowerCase()
    if (!normalizedPriority) continue

    if (normalizedLabel.includes(normalizedPriority)) {
      return index
    }

    if (
      tokenizeComparisonPriority(normalizedPriority).some((token) =>
        normalizedLabel.includes(token)
      )
    ) {
      return index
    }
  }

  return Number.MAX_SAFE_INTEGER
}

function buildProductComparisonRows(
  document: CanonicalResearchReportDocument,
  topProducts: CanonicalReportProduct[]
): SeoComparisonTable['rows'] {
  const rows: SeoComparisonTable['rows'] = []
  const productSummariesBySlug = new Map(
    (document.analysis?.productSummaries ?? []).map((summary) => [
      summary.slug,
      summary,
    ])
  )

  const pushRow = (label: string, values: Array<string | null>) => {
    if (rows.length >= COMPARISON_TABLE_MAX_ROWS) return
    if (values.filter((value) => value != null).length < 2) return
    if (rows.some((row) => row.label === label)) return
    rows.push({ label, values })
  }

  pushRow(
    'Price range',
    topProducts.map((product) => formatProductPriceRange(product))
  )
  pushRow(
    'Best for',
    topProducts.map((product) => {
      const summary = productSummariesBySlug.get(product.slug)
      return firstComparisonValue([
        product.assessment?.bestFor?.[0],
        summary?.bestFor?.[0],
        product.ranking?.highlightText,
      ])
    })
  )
  pushRow(
    'Standout feature',
    topProducts.map((product) => {
      const summary = productSummariesBySlug.get(product.slug)
      return firstComparisonValue([
        product.assessment?.standoutFeatures?.[0],
        summary?.standoutFeatures?.[0],
        product.assessment?.pros?.[0],
        product.ranking?.whyCommunity?.[0],
      ])
    })
  )
  pushRow(
    'Main tradeoff',
    topProducts.map((product) => {
      const summary = productSummariesBySlug.get(product.slug)
      return firstComparisonValue([
        product.assessment?.cons?.[0],
        summary?.tradeoffs?.[0],
        product.assessment?.notFor?.[0],
        summary?.notFor?.[0],
        product.ranking?.worthKnowing?.[0],
      ])
    })
  )

  const remainingSlots = COMPARISON_TABLE_MAX_ROWS - rows.length
  if (remainingSlots > 0) {
    const priorityLabels = buildComparisonPriorityLabels(document)
    const specCounts = new Map<string, number>()
    const specLabels = new Map<string, string>()

    for (const product of topProducts) {
      for (const key of Object.keys(product.specs ?? {})) {
        const normalized = normalizeComparisonSpecKey(key)
        if (!normalized || COMPARISON_TABLE_SKIP_SPEC_KEYS.has(normalized)) {
          continue
        }

        specCounts.set(normalized, (specCounts.get(normalized) ?? 0) + 1)
        if (!specLabels.has(normalized)) {
          specLabels.set(
            normalized,
            formatComparisonLabel(key.replace(/\s*\([^)]*\)\s*$/, ''))
          )
        }
      }
    }

    const minCoverage = Math.ceil(topProducts.length / 2)
    const specKeys = Array.from(specCounts.entries())
      .filter(([, count]) => count >= minCoverage)
      .sort((a, b) => {
        const labelA = specLabels.get(a[0]) ?? a[0]
        const labelB = specLabels.get(b[0]) ?? b[0]
        const priorityA = scoreComparisonPriority(labelA, priorityLabels)
        const priorityB = scoreComparisonPriority(labelB, priorityLabels)
        if (priorityA !== priorityB) return priorityB - priorityA
        const priorityIndexA = getComparisonPriorityIndex(
          labelA,
          priorityLabels
        )
        const priorityIndexB = getComparisonPriorityIndex(
          labelB,
          priorityLabels
        )
        if (priorityIndexA !== priorityIndexB) {
          return priorityIndexA - priorityIndexB
        }
        if (a[1] !== b[1]) return b[1] - a[1]
        return labelA.localeCompare(labelB)
      })
      .slice(0, remainingSlots)
      .map(([key]) => key)

    for (const specKey of specKeys) {
      const label = specLabels.get(specKey) ?? specKey
      pushRow(
        label,
        topProducts.map((product) => {
          const entry = Object.entries(product.specs ?? {}).find(
            ([key]) => normalizeComparisonSpecKey(key) === specKey
          )
          return formatComparisonValue(entry?.[1])
        })
      )
    }
  }

  return rows
}

function buildDimensionComparisonRows(
  document: CanonicalResearchReportDocument,
  topProducts: CanonicalReportProduct[],
  options: { omitRelativeScores: boolean }
): SeoComparisonTable['rows'] {
  const dims = (document.analysis?.comparisonDimensions ?? []).filter(
    (dim) => !(options.omitRelativeScores && dim.kind === 'relative')
  )
  if (dims.length === 0) return []

  return dims.map((dim) => ({
    label: dim.label,
    values: topProducts.map((product) => {
      const entry = dim.values.find((v) => v.slug === product.slug)
      if (!entry) return null
      if (dim.kind === 'absolute' && entry.value != null) {
        return dim.unit ? `${entry.value} ${dim.unit}` : String(entry.value)
      }
      if (dim.kind === 'relative' && entry.score != null) {
        return `${entry.score}/100`
      }
      const spec = product.specs?.[dim.key]
      if (spec != null) return String(spec)
      return null
    }),
  }))
}

function buildComparisonTable(
  document: CanonicalResearchReportDocument,
  rankedProducts: CanonicalReportProduct[],
  options: { omitRelativeScores: boolean }
): SeoComparisonTable | undefined {
  const topProducts = rankedProducts.slice(0, 4)
  if (topProducts.length < 2) return undefined

  const columns = topProducts.map((p, i) => ({
    slug: p.slug,
    name: p.name,
    isHighlighted: i === 0,
  }))

  const productRows = buildProductComparisonRows(document, topProducts)
  const rows =
    productRows.length >= 2
      ? productRows
      : buildDimensionComparisonRows(document, topProducts, options)

  if (rows.length === 0) return undefined

  return { columns, rows }
}

function buildSkipProduct(
  rankedProducts: CanonicalReportProduct[]
): SeoSkipProduct | undefined {
  const candidates = rankedProducts
    .filter(
      (p) =>
        (p.assessment?.cons?.length ?? 0) >= 3 && p.assessment?.summary != null
    )
    .sort((a, b) => {
      const rankA = a.ranking?.rank ?? Number.MAX_SAFE_INTEGER
      const rankB = b.ranking?.rank ?? Number.MAX_SAFE_INTEGER
      return rankB - rankA
    })

  const pick = candidates[0]
  if (!pick) return undefined

  return {
    slug: pick.slug,
    name: pick.name,
    image: pick.images[0] ?? null,
    summary: pick.assessment?.summary ?? '',
    reasons: (pick.assessment?.cons ?? []).slice(0, 4),
  }
}

// Notable mentions = ranked products beyond the top picks, capped at 3.
// TODO(notable-mentions): also surface non-shortlisted candidates from the
// research-task pool so reports with <=topPickCount+3 ranked products can
// still show three mentions. Requires plumbing rejected candidates onto the
// canonical document — not in the projection today.
const NOTABLE_MENTIONS_CAP = 3

function buildNotableMentions(
  rankedProducts: CanonicalReportProduct[],
  topPickCount: number
): SeoNotableMention[] {
  // Pulls the next NOTABLE_MENTIONS_CAP ranked products beyond the top picks.
  // We don't gate on `assessment.summary` here — even an under-detailed mention
  // is a legitimate signal to surface.
  return rankedProducts
    .slice(topPickCount)
    .slice(0, NOTABLE_MENTIONS_CAP)
    .map((p) => {
      const firstBuyLink = p.retailerLinks?.buyLinks?.[0]
      return {
        slug: p.slug,
        name: p.name,
        image: p.images[0] ?? null,
        summary: p.assessment?.summary ?? '',
        matchScore: p.ranking?.fitScore ?? null,
        pros: (p.assessment?.pros ?? []).slice(0, 2),
        buyLink: firstBuyLink
          ? {
              label: firstBuyLink.label,
              url: firstBuyLink.url,
              price: firstBuyLink.price ?? null,
            }
          : null,
        pricing: p.pricing ?? null,
      }
    })
}

// The author is the organizational research team (a real entity with a
// public page), not a Person. Person nodes for invented personas were a
// QRG-deception liability and were removed in June 2026.
function buildAuthorJsonLd(author: ResearchAuthor): Record<string, unknown> {
  return {
    '@type': 'Organization',
    name: author.name,
    url: author.url,
    description: author.bio,
    logo: {
      '@type': 'ImageObject',
      url: author.avatarUrl,
    },
  }
}

// Retail domains are excluded from the citation list — they're commerce
// references, not research evidence. Kept in lockstep with the web component
// `SeoReportSources.RETAIL_DOMAIN_HINTS` so the on-page footer and the
// JSON-LD citations describe the same corpus of "research links".
const RETAIL_DOMAIN_HINTS = [
  'amazon.',
  'bestbuy.',
  'bloomingdales.',
  'costco.',
  'ebay.',
  'etsy.',
  'macys.',
  'nordstrom.',
  'saksfifthavenue.',
  'sephora.',
  'target.',
  'ulta.',
  'walmart.',
  'wayfair.',
]

function isRetailUrl(url: string): boolean {
  const lower = url.toLowerCase()
  return RETAIL_DOMAIN_HINTS.some((hint) => lower.includes(hint))
}

function normalizeCitationUrlKey(url: string): string {
  try {
    const parsed = new URL(url)
    parsed.hash = ''
    return parsed.toString().replace(/\/$/, '').toLowerCase()
  } catch {
    return url.trim().toLowerCase()
  }
}

function buildCitationsJsonLd(
  document: CanonicalResearchReportDocument
): Record<string, unknown>[] {
  const seen = new Set<string>()
  const citations: Record<string, unknown>[] = []

  const push = (
    url: string | undefined | null,
    name: string | null | undefined
  ) => {
    const trimmed = typeof url === 'string' ? url.trim() : ''
    if (!trimmed) return
    if (isRetailUrl(trimmed)) return
    const key = normalizeCitationUrlKey(trimmed)
    if (seen.has(key)) return
    seen.add(key)
    const cleanName = normalizeNullableString(name)
    citations.push({
      '@type': 'CreativeWork',
      url: trimmed,
      ...(cleanName ? { name: cleanName } : {}),
    })
  }

  for (const product of document.products) {
    for (const source of product.sources ?? []) {
      push(source.url, source.label)
    }
    for (const video of product.socialEvidence?.videos ?? []) {
      push(video.url, video.label)
    }
    for (const thread of product.socialEvidence?.redditThreads ?? []) {
      push(thread.url, thread.title)
    }
  }

  return citations
}

function buildArticleJsonLd(args: {
  title: string
  description: string
  canonicalUrl: string
  author: ResearchAuthor
  publishedAt: string | null | undefined
  lastUpdated: string | null | undefined
  heroImageUrl: string | null | undefined
  citations: Record<string, unknown>[]
}): Record<string, unknown> {
  const {
    title,
    description,
    canonicalUrl,
    author,
    publishedAt,
    lastUpdated,
    heroImageUrl,
    citations,
  } = args
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: title,
    description,
    url: canonicalUrl,
    mainEntityOfPage: { '@type': 'WebPage', '@id': canonicalUrl },
    ...(heroImageUrl
      ? {
          image: {
            '@type': 'ImageObject',
            url: heroImageUrl,
            width: 1200,
            height: 630,
          },
        }
      : {}),
    ...(publishedAt ? { datePublished: publishedAt } : {}),
    ...(lastUpdated ? { dateModified: lastUpdated } : {}),
    author: buildAuthorJsonLd(author),
    publisher: {
      '@type': 'Organization',
      name: 'Dupe',
      logo: {
        '@type': 'ImageObject',
        url: 'https://dupe.com/android-chrome-512x512.png',
        width: 512,
        height: 512,
      },
    },
    ...(citations.length > 0 ? { citation: citations } : {}),
  }
}

function readNumericField(
  sources: Array<Record<string, string | number | boolean | null> | undefined>,
  patterns: RegExp[]
): number | null {
  for (const source of sources) {
    if (!source) continue
    for (const [key, raw] of Object.entries(source)) {
      if (!patterns.some((pattern) => pattern.test(key))) continue
      const value = typeof raw === 'number' ? raw : Number(raw)
      if (Number.isFinite(value)) return value
    }
  }
  return null
}

const CURRENCY_SYMBOL_TO_CODE: Record<string, string> = {
  '£': 'GBP',
  '€': 'EUR',
  $: 'USD',
  '¥': 'JPY',
  '₹': 'INR',
}

/** Detect ISO currency code from a free-text price string by looking for a
 *  currency symbol. Returns null when no symbol is present. */
function detectCurrencyFromString(input: string): string | null {
  for (const ch of input) {
    if (CURRENCY_SYMBOL_TO_CODE[ch]) return CURRENCY_SYMBOL_TO_CODE[ch]
  }
  return null
}

/** Best-effort priceCurrency derivation:
 *  1. trust pricing.currency if it looks like a real ISO code
 *  2. scan price-like attribute/spec string fields for a currency symbol
 *  3. fall back to scanning the assessment summary (LLM often quotes original
 *     retailer currency, e.g. "£69-£89")
 *  4. default USD
 *  Upstream pipeline defaults pricing.currency to "USD" so we cannot tell a
 *  real USD from a defaulted-USD; the symbol scan is what catches UK/EU
 *  retailers whose numeric price was scraped without proper currency
 *  attribution. */
function derivePriceCurrency(product: CanonicalReportProduct): string {
  const explicit = normalizeNullableString(product.pricing.currency)
  if (explicit && explicit.toUpperCase() !== 'USD') {
    return explicit.toUpperCase()
  }

  const priceLikeKey = /price|msrp|cost/i
  for (const source of [product.attributes, product.specs]) {
    if (!source) continue
    for (const [key, raw] of Object.entries(source)) {
      if (!priceLikeKey.test(key)) continue
      if (typeof raw !== 'string') continue
      const detected = detectCurrencyFromString(raw)
      if (detected) return detected
    }
  }

  const summary = product.assessment?.summary
  if (summary) {
    const detected = detectCurrencyFromString(summary.slice(0, 400))
    if (detected) return detected
  }

  return explicit ? explicit.toUpperCase() : 'USD'
}

/** ISO 8601 date one year from `now`, used as offers.priceValidUntil. */
function priceValidUntil(now: Date): string {
  const d = new Date(now)
  d.setUTCFullYear(d.getUTCFullYear() + 1)
  return d.toISOString().slice(0, 10)
}

function deriveSeller(
  product: CanonicalReportProduct
): Record<string, string> | null {
  const buyLinkLabel = normalizeNullableString(
    product.retailerLinks?.buyLinks?.[0]?.label
  )
  if (buyLinkLabel) return { '@type': 'Organization', name: buyLinkLabel }
  if (product.brand) return { '@type': 'Organization', name: product.brand }
  return null
}

function buildReviewNotesJsonLd(
  values: string[] | undefined
): Record<string, unknown> | null {
  const notes = uniqueStrings(values ?? []).slice(0, 5)
  if (notes.length === 0) return null

  return {
    '@type': 'ItemList',
    itemListElement: notes.map((note, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: note,
    })),
  }
}

function buildProductEditorialReviewJsonLd(args: {
  product: CanonicalReportProduct
  author: ResearchAuthor
  publishedAt: string | null | undefined
  lastUpdated: string | null | undefined
}): Record<string, unknown> | null {
  const { product, author, publishedAt, lastUpdated } = args
  // Only products the ranker scored get a review; fragrance reports null
  // every fitScore, so they never get one.
  const fitScore = product.ranking?.fitScore
  if (fitScore == null || !Number.isFinite(fitScore)) return null

  const reviewBody =
    normalizeNullableString(product.assessment?.summary) ??
    normalizeNullableString(product.ranking?.rationale) ??
    normalizeNullableString(product.ranking?.highlightText)
  if (!reviewBody) return null

  // fitScore only places a product within its own report (best ~90, weakest
  // near 0), so it is never published as a rating. Without a rating, Google
  // only accepts the Review as a pros/cons editorial review, which needs at
  // least two statements across pros and cons.
  const pros = uniqueStrings(product.assessment?.pros ?? [])
  const cons = uniqueStrings(product.assessment?.cons ?? [])
  if (pros.length + cons.length < 2) return null
  const positiveNotes = buildReviewNotesJsonLd(pros)
  const negativeNotes = buildReviewNotesJsonLd(cons)

  return {
    '@type': 'Review',
    name: `Dupe editorial review of ${product.name}`,
    author: buildAuthorJsonLd(author),
    reviewBody,
    ...(publishedAt ? { datePublished: publishedAt } : {}),
    ...(lastUpdated ? { dateModified: lastUpdated } : {}),
    publisher: {
      '@type': 'Organization',
      name: 'Dupe',
    },
    ...(positiveNotes ? { positiveNotes } : {}),
    ...(negativeNotes ? { negativeNotes } : {}),
  }
}

function buildProductJsonLd(
  product: CanonicalReportProduct,
  canonicalUrl: string,
  author: ResearchAuthor,
  now: Date,
  reviewDates: {
    publishedAt: string | null | undefined
    lastUpdated: string | null | undefined
  }
): Record<string, unknown> {
  const productUrl = `${canonicalUrl}#${product.slug}`
  const currency = derivePriceCurrency(product)
  const explicitCurrency = normalizeNullableString(product.pricing.currency)
  // Only emit a numeric price when the upstream pricing.currency matches what
  // we ended up using -- otherwise we'd pair a number scraped in one currency
  // with the symbol of another and trip Google's currency-mismatch suppression.
  const priceMatchesCurrency =
    !!explicitCurrency && explicitCurrency.toUpperCase() === currency
  const price = priceMatchesCurrency
    ? (product.pricing.observedLow ??
      product.pricing.msrp ??
      product.pricing.observedHigh)
    : null
  // Monetized /redirect/ buy links are robots-disallowed, so they must not
  // be emitted as Offer.url (schema pointing at uncrawlable URLs). Use the
  // buy link only when it is a direct external URL; otherwise anchor the
  // offer to the product section of this page.
  const firstBuyLinkUrl =
    normalizeNullableString(product.retailerLinks?.buyLinks?.[0]?.url) ?? null
  const isMonetizedRedirect =
    !!firstBuyLinkUrl &&
    (firstBuyLinkUrl.startsWith('/redirect') ||
      firstBuyLinkUrl.includes('dupe.com/redirect'))
  const buyLink =
    firstBuyLinkUrl && !isMonetizedRedirect ? firstBuyLinkUrl : productUrl
  const ratingValue = readNumericField(
    [product.attributes, product.specs],
    [/^rating$/i, /average.*rating/i, /avg.*review/i, /user.*rating/i]
  )
  const reviewCount = readNumericField(
    [product.attributes, product.specs],
    [/review.*count/i, /reviews$/i, /rating.*count/i]
  )
  const seller = deriveSeller(product)
  const editorialReview = buildProductEditorialReviewJsonLd({
    product,
    author,
    ...reviewDates,
  })

  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    '@id': productUrl,
    name: product.name,
    url: productUrl,
    sku: product.slug,
    ...(product.brand
      ? { brand: { '@type': 'Brand', name: product.brand } }
      : {}),
    ...(product.images.length > 0 ? { image: product.images.slice(0, 5) } : {}),
    ...(product.assessment?.summary
      ? { description: product.assessment.summary }
      : {}),
    offers: {
      '@type': 'Offer',
      url: buyLink,
      priceCurrency: currency,
      ...(price != null ? { price } : {}),
      priceValidUntil: priceValidUntil(now),
      availability: 'https://schema.org/InStock',
      ...(seller ? { seller } : {}),
    },
    // Prefer real sourced aggregateRating when present. Dupe's internal
    // editorial fitScore is exposed only as a nested Product.review below, not
    // as aggregateRating, because it is not crowd consensus.
    ...(ratingValue != null && reviewCount != null && reviewCount > 0
      ? {
          aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue,
            reviewCount,
          },
        }
      : {}),
    ...(editorialReview ? { review: editorialReview } : {}),
  }
}

function buildSections(
  document: CanonicalResearchReportDocument,
  rankedProducts: CanonicalReportProduct[],
  faq: SeoFaqEntry[],
  relatedLinks: SeoProjection['relatedLinks'],
  skipProduct: SeoSkipProduct | undefined,
  notableMentions: SeoNotableMention[],
  comparisonTable: SeoComparisonTable | undefined,
  visualSignals: SeoReportVisualSignalsContent | undefined,
  whoItsFor: { body: string } | null | undefined
): SeoProjection['sections'] {
  const sections: SeoProjection['sections'] = [
    { type: 'quick-answer', title: 'Intro', id: 'quick-answer' },
    { type: 'top-picks', title: 'All of our top picks', id: 'top-picks' },
  ]

  if (whoItsFor) {
    sections.push({
      type: 'who-its-for',
      title: 'Who this is for',
      id: 'who-this-is-for',
    })
  }

  sections.push({
    type: 'how-we-picked',
    title: 'How we picked',
    id: 'how-we-picked',
  })

  for (const product of rankedProducts.slice(0, 4)) {
    sections.push({
      type: 'ranked-product',
      title: product.name,
      id: product.slug,
    })
  }

  if (notableMentions.length > 0) {
    sections.push({
      type: 'notable-mentions',
      title: 'Notable mentions',
      id: 'notable-mentions',
    })
  }

  if (comparisonTable) {
    sections.push({
      type: 'comparison-table',
      title: 'Key spec comparison',
      id: 'key-spec-comparison',
    })
  }

  if (visualSignals) {
    sections.push({
      type: 'visual-signals',
      title: visualSignals.sectionTitle ?? 'Visual signals',
      id: 'visual-signals',
    })
  }

  if (faq.length > 0) {
    sections.push({
      type: 'what-to-know',
      title: 'What to know before buying',
      id: 'what-to-know',
    })
  }

  if (skipProduct) {
    sections.push({
      type: 'skip-product',
      title: 'Skip this one',
      id: 'skip-this-one',
    })
  }

  if (relatedLinks.length > 0) {
    sections.push({
      type: 'related-links',
      title: 'Related reports',
      id: 'related-reports',
    })
  }

  return sections
}

export function projectSeoReport(args: {
  document: CanonicalResearchReportDocument
  slug: string
  keyword?: string | null
  categorySlug?: string | null
  subcategorySlug?: string | null
  categoryTaxonomyVersion?: string | null
  relatedLinks?: SeoProjection['relatedLinks']
  overrides?: ResearchReportAdminOverrides | null
  generatedComponents?: SeoGeneratedComponents | null
  generatedProductImages?: SeoGeneratedProductImages | null
  publicBaseUrl?: string
  publishedAt?: string | null
  updatedAt?: string | null
}): SeoProjection {
  const {
    document,
    slug,
    keyword = null,
    categorySlug = null,
    subcategorySlug = null,
    categoryTaxonomyVersion = null,
    relatedLinks = [],
    overrides = null,
    generatedComponents = null,
    generatedProductImages = null,
    publicBaseUrl = 'https://dupe.com',
    publishedAt = null,
    updatedAt = null,
  } = args

  const normalizedBaseUrl = publicBaseUrl.replace(/\/$/, '')
  const inheritedCategorySlug =
    categorySlug ??
    document.meta.categorySlug ??
    generatedComponents?.category_classification?.categorySlug ??
    null
  const reportCategorySlug =
    overrides?.categorySlugOverride ?? inheritedCategorySlug
  const inheritedSubcategorySlug = resolveReportSubcategorySlug({
    subcategorySlug,
    document,
    generatedComponents,
  })
  // Keyed on the report's own subcategory, not `reportSubcategorySlug`: an
  // admin category override nulls the projected one, and that must never
  // bring fragrance scores back.
  const isFragrance = inheritedSubcategorySlug === FRAGRANCE_SUBCATEGORY_SLUG
  const reportSubcategorySlug =
    overrides?.categorySlugOverride &&
    overrides.categorySlugOverride !== inheritedCategorySlug
      ? null
      : inheritedSubcategorySlug
  const canonicalUrl = buildSeoReportUrl({
    baseUrl: normalizedBaseUrl,
    slug,
    categorySlug: reportCategorySlug,
    subcategorySlug: reportSubcategorySlug,
  })
  const reportAuthor = getResearchAuthor(reportCategorySlug)

  const sortedProducts = rankProducts(document.products)
  // Drop spec keys that are uniformly "negative" across the report
  // (e.g. `clogs: No` on every shoe in a non-clogs report) — those are
  // upstream taxonomy classifier outputs that pollute the key-specs
  // table without adding any signal. Computed once over the full product
  // list and reused per product.
  const uninformativeSpecKeys = findUninformativeSpecKeys(sortedProducts)
  const rankedProducts = overlayGeneratedProductImages(
    sortedProducts.map((product) => {
      // Omit `specs` entirely when undefined — Next.js getStaticProps rejects
      // explicit `undefined` values during JSON serialization. The type is
      // already optional, so "absent" is a valid shape.
      const specs = dropTaxonomyClassifierSpecs(
        product.specs,
        uninformativeSpecKeys
      )
      const { specs: _omit, ...rest } = product
      const withDurableImages = {
        ...rest,
        images: preferDurableImages(rest.images),
      }
      const base: CanonicalReportProduct =
        specs !== undefined
          ? { ...withDurableImages, specs }
          : withDurableImages
      if (!product.ranking) return base
      // Never invent a score from list position: it surfaced as a fake
      // "% match" badge, "Community score" row and JSON-LD rating.
      return {
        ...base,
        ranking: {
          ...product.ranking,
          fitScore: isFragrance ? null : (product.ranking.fitScore ?? null),
        },
      }
    }),
    generatedProductImages
  )
  const topPick = rankedProducts[0]
  const generatedQuickAnswer = buildGeneratedQuickAnswer(generatedComponents)
  const quickAnswer =
    generatedQuickAnswer ?? buildQuickAnswer(document, topPick)
  const generatedFaq = buildGeneratedFaq(generatedComponents)
  const faq = sanitizeFaqEntries(
    generatedFaq.length > 0 ? generatedFaq : buildFaq(document)
  )
  const h1 = buildH1(document, keyword, overrides)
  const title = buildSeoTitle(
    document,
    keyword,
    h1,
    overrides,
    generatedComponents
  )
  const description = buildMetaDescription(
    document,
    keyword,
    overrides,
    generatedComponents
  )
  const heroImageUrl = generatedComponents?.hero_image?.assetUrl ?? null
  const heroImage =
    heroImageUrl != null
      ? {
          url: heroImageUrl,
          alt: `${h1} hero image`,
          sourceProductSlugs:
            generatedComponents?.hero_image?.sourceProductSlugs ?? [],
          generatedAt: generatedComponents?.hero_image?.generatedAt ?? null,
        }
      : undefined
  const evidence = buildEvidence(document)
  const freshnessStatement = buildFreshnessStatement(document)
  const archetype = buildArchetype(document)
  const visualSignals = isFragrance
    ? undefined
    : buildGeneratedVisualSignals(generatedComponents)
  const comparisonTable = buildComparisonTable(document, rankedProducts, {
    omitRelativeScores: isFragrance,
  })
  const skipProduct = buildSkipProduct(rankedProducts)
  const notableMentions = buildNotableMentions(rankedProducts, 4)
  const generatedHowWePicked =
    generatedComponents?.how_we_picked?.status === 'ready'
      ? generatedComponents.how_we_picked.content
      : null
  // The "Who this is for" component title is a static frontend label; only
  // the body is LLM-generated. Strip any legacy title that may still live on
  // previously-persisted content so the projection always emits a body-only
  // shape.
  const generatedWhoItsForBody =
    generatedComponents?.who_its_for?.status === 'ready'
      ? generatedComponents.who_its_for.content?.body
      : null
  const generatedWhoItsFor = generatedWhoItsForBody
    ? { body: generatedWhoItsForBody }
    : null
  const methodology = document.methodology
    ? {
        title: 'How we picked the best',
        intro:
          generatedHowWePicked?.intro ??
          document.methodology.brief?.strategy ??
          null,
        whoItsFor: generatedWhoItsFor ?? null,
        guide: generatedHowWePicked?.criteria
          ? {
              ...document.methodology.buyingGuide,
              eyebrow:
                document.methodology.buyingGuide?.eyebrow ?? 'Evaluation',
              title: document.methodology.buyingGuide?.title ?? 'How we picked',
              insights: generatedHowWePicked.criteria.map((c) => ({
                heading: c.label,
                body: c.description,
                icon: c.icon,
              })),
              callout: document.methodology.buyingGuide?.callout ?? {
                title: '',
                body: '',
              },
            }
          : (document.methodology.buyingGuide ?? null),
      }
    : null
  const faqPageJsonLd =
    faq.length > 0
      ? {
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: faq.map((entry) => ({
            '@type': 'Question',
            name: entry.question,
            acceptedAnswer: {
              '@type': 'Answer',
              text: entry.answer,
            },
          })),
        }
      : null
  const breadcrumbItems = [
    {
      '@type': 'ListItem',
      position: 1,
      name: 'Home',
      item: `${normalizedBaseUrl}/`,
    },
    {
      '@type': 'ListItem',
      position: 2,
      name: 'Reports',
      item: `${normalizedBaseUrl}/reports`,
    },
  ] as Array<Record<string, unknown>>

  if (reportCategorySlug) {
    breadcrumbItems.push({
      '@type': 'ListItem',
      position: breadcrumbItems.length + 1,
      name:
        getSeoReportCategory(reportCategorySlug)?.label ?? reportCategorySlug,
      item: `${normalizedBaseUrl}/reports/category/${reportCategorySlug}`,
    })
  }

  if (reportCategorySlug && reportSubcategorySlug) {
    breadcrumbItems.push({
      '@type': 'ListItem',
      position: breadcrumbItems.length + 1,
      name:
        getSeoReportSubcategory(reportCategorySlug, reportSubcategorySlug)
          ?.label ?? reportSubcategorySlug,
      item: `${normalizedBaseUrl}/reports/category/${reportCategorySlug}/${reportSubcategorySlug}`,
    })
  }

  const breadcrumbListJsonLd = reportCategorySlug
    ? {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          ...breadcrumbItems,
          {
            '@type': 'ListItem',
            position: breadcrumbItems.length + 1,
            name: h1,
            item: canonicalUrl,
          },
        ],
      }
    : null

  const lastUpdated =
    updatedAt ??
    document.sourceSnapshot.taskCompletedAt ??
    document.meta.completedAt ??
    null
  // datePublished should reflect the first time the report was made public.
  // Fall back to the document task-completion date when the publish wrapper
  // hasn't supplied one (e.g. preview rendering paths).
  const articlePublishedAt =
    publishedAt ??
    document.sourceSnapshot.taskCompletedAt ??
    document.meta.completedAt ??
    null

  return {
    version: '1',
    meta: {
      title,
      description,
      h1,
      categorySlug: overrides?.categorySlugOverride ?? reportCategorySlug,
      subcategorySlug: reportSubcategorySlug,
      categoryTaxonomyVersion:
        categoryTaxonomyVersion ??
        document.meta.categoryTaxonomyVersion ??
        generatedComponents?.category_classification?.categoryTaxonomyVersion ??
        null,
      keyword,
      lastUpdated,
      freshnessStatement,
    },
    ...(heroImage ? { heroImage } : {}),
    ...(quickAnswer ? { quickAnswer } : {}),
    ...(archetype ? { archetype } : {}),
    evidence,
    topPicks: (() => {
      const topPickSlice = rankedProducts.slice(0, 4)
      const roles = buildTopPickRoles(topPickSlice)
      // Overlay generated short descriptors if available
      const generatedDescriptors = new Map(
        (generatedComponents?.top_pick_descriptors?.descriptors ?? []).map(
          (d) => [d.slug, d.shortDescriptor]
        )
      )
      return topPickSlice.map((product, index) => {
        const role = roles[index] ?? 'alternative'
        const summary =
          document.analysis?.productSummaries?.find(
            (entry) => entry.slug === product.slug
          )?.summary ??
          product.assessment?.summary ??
          product.ranking?.highlightText ??
          product.ranking?.rationale ??
          null

        return {
          slug: product.slug,
          role,
          roleLabel: buildRoleLabel(role),
          roleDescription:
            generatedDescriptors.get(product.slug) ??
            buildTopPickRoleDescription(product, summary),
          name: product.name,
          brand: product.brand ?? null,
          summary,
          image: product.images[0] ?? null,
          pricing: product.pricing,
          buyLinks: buildTopPickBuyLinks(product),
        }
      })
    })(),
    rankedProducts,
    ...(notableMentions.length > 0 ? { notableMentions } : {}),
    ...(visualSignals ? { visualSignals } : {}),
    ...(comparisonTable ? { comparisonTable } : {}),
    ...(methodology ? { methodology } : {}),
    ...(skipProduct ? { skipProduct } : {}),
    faq,
    relatedLinks,
    sections: buildSections(
      document,
      rankedProducts,
      faq,
      relatedLinks,
      skipProduct,
      notableMentions,
      comparisonTable,
      visualSignals,
      methodology?.whoItsFor ?? null
    ),
    jsonLd: (() => {
      const now = new Date()
      return {
        itemList: {
          '@context': 'https://schema.org',
          '@type': 'ItemList',
          itemListElement: rankedProducts
            .slice(0, 10)
            .map((product, index) => ({
              '@type': 'ListItem',
              position: index + 1,
              // Bind to the Product node @id so Google de-duplicates the
              // ItemList against the Product cluster.
              item: {
                '@id': `${canonicalUrl}#${product.slug}`,
                '@type': 'Product',
                name: product.name,
                url: `${canonicalUrl}#${product.slug}`,
              },
            })),
        },
        products: rankedProducts.slice(0, 10).map((product) =>
          buildProductJsonLd(product, canonicalUrl, reportAuthor, now, {
            publishedAt: articlePublishedAt,
            lastUpdated,
          })
        ),
        ...(faqPageJsonLd ? { faqPage: faqPageJsonLd } : {}),
        ...(breadcrumbListJsonLd
          ? { breadcrumbList: breadcrumbListJsonLd }
          : {}),
        article: buildArticleJsonLd({
          title,
          description,
          canonicalUrl,
          author: reportAuthor,
          publishedAt: articlePublishedAt,
          lastUpdated,
          heroImageUrl,
          citations: buildCitationsJsonLd(document),
        }),
      }
    })(),
  }
}
